import React, { useEffect, useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ImageUploader from "@/components/admin/ImageUploader";
import { toast } from "sonner";
import {
  LogOut, Plus, Pencil, Trash2, Calendar, MapPin, Users, Loader2, RefreshCw,
  MessageSquare, GraduationCap, Handshake, FileText, BarChart3, Quote, Eye, EyeOff,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_EVENT = {
  title: "", description: "", event_date: "", location: "",
  category: "", image_url: "", capacity: "", is_published: true,
};

const EMPTY_TESTIMONIAL = {
  name: "", role: "", country: "", text: "",
  avatar_url: "", is_published: true, order: 0,
};

const TestimonialForm = ({ initial, onSubmit, submitting, authHeader }) => {
  const [form, setForm] = useState({ ...EMPTY_TESTIMONIAL, ...initial });
  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = (e) => {
    e.preventDefault();
    const payload = { ...form };
    payload.order = Number(payload.order) || 0;
    if (!payload.avatar_url) payload.avatar_url = null;
    if (!payload.country) payload.country = null;
    onSubmit(payload);
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="admin-testimonial-form">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold">Nom complet *</Label>
          <Input value={form.name} required onChange={(e) => set("name", e.target.value)}
            className="mt-2 rounded-none border-navy-100" data-testid="admin-testimonial-name" />
        </div>
        <div>
          <Label className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold">Rôle / Formation *</Label>
          <Input value={form.role} required onChange={(e) => set("role", e.target.value)}
            placeholder="ex: Étudiant MBA · Diplômé BBA3 · Étudiante MBA Executive"
            className="mt-2 rounded-none border-navy-100" data-testid="admin-testimonial-role" />
        </div>
      </div>
      <div>
        <Label className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold">Pays</Label>
        <Input value={form.country || ""} onChange={(e) => set("country", e.target.value)}
          placeholder="ex: Côte d'Ivoire" className="mt-2 rounded-none border-navy-100"
          data-testid="admin-testimonial-country" />
      </div>
      <div>
        <Label className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold">Témoignage *</Label>
        <Textarea required rows={4} value={form.text} maxLength={2000} onChange={(e) => set("text", e.target.value)}
          className="mt-2 rounded-none border-navy-100 resize-none"
          data-testid="admin-testimonial-text" />
        <div className="text-[10px] text-[#888] mt-1">{form.text.length}/2000</div>
      </div>
      <div>
        <Label className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold mb-2 block">Photo (téléversement)</Label>
        <ImageUploader
          value={form.avatar_url || null}
          onChange={(url) => set("avatar_url", url || "")}
          folder="testimonials"
          authHeader={authHeader}
          testId="admin-testimonial-avatar"
        />
      </div>
      <div className="grid md:grid-cols-2 gap-4 items-end">
        <div>
          <Label className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold">Ordre d'affichage</Label>
          <Input type="number" value={form.order} onChange={(e) => set("order", e.target.value)}
            className="mt-2 rounded-none border-navy-100"
            data-testid="admin-testimonial-order" />
        </div>
        <div className="flex items-center gap-3 pb-2">
          <Switch checked={form.is_published} onCheckedChange={(v) => set("is_published", v)}
            data-testid="admin-testimonial-published" />
          <Label className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold">
            {form.is_published ? "Publié sur le site" : "Brouillon (non visible)"}
          </Label>
        </div>
      </div>
      <button type="submit" disabled={submitting} className="btn-red w-full justify-center" data-testid="admin-testimonial-submit">
        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
        {submitting ? "Enregistrement…" : "Enregistrer"}
      </button>
    </form>
  );
};

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div data-testid={testId} className="bg-white border border-navy-100 p-5">
    <div className="flex items-center justify-between">
      <Icon className="h-5 w-5 text-navy-500" strokeWidth={1.5} />
      <div className="overline text-navy-500">{label}</div>
    </div>
    <div className="stat-num text-4xl text-navy-700 mt-3">{value ?? "…"}</div>
  </div>
);

const EventForm = ({ initial, onSubmit, onClose, submitting, authHeader }) => {
  const [form, setForm] = useState({ ...EMPTY_EVENT, ...initial });
  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (payload.capacity === "" || payload.capacity == null) payload.capacity = null;
    else payload.capacity = Number(payload.capacity);
    if (!payload.image_url) payload.image_url = null;
    if (!payload.category) payload.category = "event";
    onSubmit(payload);
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="admin-event-form">
      <div>
        <Label className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold">Titre *</Label>
        <Input value={form.title} required onChange={(e) => set("title", e.target.value)} className="mt-2 rounded-none border-navy-100" data-testid="admin-event-title" />
      </div>
      <div>
        <Label className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold">Description *</Label>
        <Textarea required rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-2 rounded-none border-navy-100 resize-none" data-testid="admin-event-description" />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold">Date *</Label>
          <Input required value={form.event_date} onChange={(e) => set("event_date", e.target.value)}
            placeholder="ex: 15 mars 2026 — 09h00"
            className="mt-2 rounded-none border-navy-100" data-testid="admin-event-date" />
        </div>
        <div>
          <Label className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold">Lieu *</Label>
          <Input required value={form.location} onChange={(e) => set("location", e.target.value)} className="mt-2 rounded-none border-navy-100" data-testid="admin-event-location" />
        </div>
        <div>
          <Label className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold">Catégorie</Label>
          <Input value={form.category} onChange={(e) => set("category", e.target.value)} placeholder="open-day, conférence, atelier…" className="mt-2 rounded-none border-navy-100" data-testid="admin-event-category" />
        </div>
        <div>
          <Label className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold">Capacité</Label>
          <Input type="number" min="0" value={form.capacity || ""} onChange={(e) => set("capacity", e.target.value)} className="mt-2 rounded-none border-navy-100" data-testid="admin-event-capacity" />
        </div>
      </div>
      <div>
        <Label className="text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold mb-2 block">Image de couverture (téléversement)</Label>
        <ImageUploader
          value={form.image_url || null}
          onChange={(url) => set("image_url", url || "")}
          folder="events"
          authHeader={authHeader}
          testId="admin-event-image"
        />
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={!!form.is_published} onCheckedChange={(v) => set("is_published", v)} data-testid="admin-event-published" />
        <Label className="text-navy-700 text-sm">Publié (visible sur le site)</Label>
      </div>
      <div className="flex gap-3 justify-end pt-2">
        <button type="button" onClick={onClose} className="btn-outline-navy" data-testid="admin-event-cancel">Annuler</button>
        <button type="submit" disabled={submitting} className="btn-red disabled:opacity-60" data-testid="admin-event-save">
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          Enregistrer
        </button>
      </div>
    </form>
  );
};

const RegistrationsModal = ({ event, authHeader }) => {
  const [regs, setRegs] = useState(null);
  useEffect(() => {
    axios.get(`${API}/events/${event.id}/registrations`, { headers: authHeader() })
      .then(r => setRegs(r.data))
      .catch(() => setRegs([]));
  }, [event.id, authHeader]);

  return (
    <div className="max-h-[60vh] overflow-y-auto" data-testid={`admin-regs-${event.id}`}>
      {regs == null ? (
        <div className="py-8 text-center text-[#888]">Chargement…</div>
      ) : regs.length === 0 ? (
        <div className="py-8 text-center text-[#888]">Aucune inscription pour le moment.</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-navy-500 border-b border-navy-100">
              <th className="py-2">Nom</th><th>Email</th><th>Téléphone</th><th>Organisation</th><th>Date</th>
            </tr>
          </thead>
          <tbody className="text-[13px]">
            {regs.map((r) => (
              <tr key={r.id} className="border-b border-navy-50">
                <td className="py-2.5 text-navy-700">{r.name}</td>
                <td>{r.email}</td>
                <td>{r.phone || "—"}</td>
                <td>{r.organization || "—"}</td>
                <td className="text-[#888]">{new Date(r.created_at).toLocaleDateString("fr-FR")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const Section = ({ title, rows, columns, testId }) => (
  <div data-testid={testId} className="bg-white border border-navy-100 mt-6">
    <div className="px-6 py-4 border-b border-navy-100 flex items-center justify-between">
      <h3 className="font-display text-xl text-navy-700">{title}</h3>
      <span className="text-xs text-[#888]">{rows.length} résultat(s)</span>
    </div>
    <div className="overflow-x-auto">
      {rows.length === 0 ? (
        <div className="p-8 text-center text-[#888] text-sm">Aucune donnée.</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-navy-500 border-b border-navy-100 bg-[#FAF8F3]">
              {columns.map((c) => <th key={c.key} className="px-6 py-3">{c.label}</th>)}
            </tr>
          </thead>
          <tbody className="text-[13px]">
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-navy-50 hover:bg-navy-50/40">
                {columns.map((c) => (
                  <td key={c.key} className="px-6 py-3 align-top text-navy-700">{c.render ? c.render(r) : (r[c.key] || "—")}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  </div>
);

export default function AdminDashboardPage() {
  const { admin, logout, authHeader } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [partners, setPartners] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showCreateTestimonial, setShowCreateTestimonial] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const headers = authHeader();
      const [statsR, evR, ctR, apR, paR, tmR] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/events?only_published=false`),
        axios.get(`${API}/contact`, { headers }),
        axios.get(`${API}/applications`, { headers }),
        axios.get(`${API}/partners`, { headers }),
        axios.get(`${API}/testimonials?only_published=false`),
      ]);
      setStats(statsR.data); setEvents(evR.data);
      setContacts(ctR.data); setApplications(apR.data); setPartners(paR.data);
      setTestimonials(tmR.data);
    } catch (err) {
      if (err?.response?.status === 401) { logout(); navigate("/admin/login"); }
    }
  }, [authHeader, logout, navigate]);

  useEffect(() => { if (admin && typeof admin === "object") fetchAll(); }, [admin, fetchAll]);

  if (admin === null) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-navy-500" /></div>;
  }
  if (!admin) return <Navigate to="/admin/login" replace />;

  const onCreate = async (payload) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/events`, payload, { headers: authHeader() });
      toast.success("Événement créé");
      setShowCreate(false);
      await fetchAll();
    } catch (err) {
      toast.error("Erreur", { description: err?.response?.data?.detail || "Veuillez réessayer." });
    } finally { setSubmitting(false); }
  };

  const onUpdate = async (payload) => {
    setSubmitting(true);
    try {
      await axios.put(`${API}/events/${editing.id}`, payload, { headers: authHeader() });
      toast.success("Événement mis à jour");
      setEditing(null);
      await fetchAll();
    } catch (err) {
      toast.error("Erreur", { description: err?.response?.data?.detail || "Veuillez réessayer." });
    } finally { setSubmitting(false); }
  };

  const onDelete = async (ev) => {
    if (!window.confirm(`Supprimer "${ev.title}" ?`)) return;
    try {
      await axios.delete(`${API}/events/${ev.id}`, { headers: authHeader() });
      toast.success("Supprimé");
      await fetchAll();
    } catch (err) {
      toast.error("Erreur", { description: err?.response?.data?.detail || "Erreur" });
    }
  };

  // ─── Testimonials CRUD ───
  const onCreateTestimonial = async (payload) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/testimonials`, payload, { headers: authHeader() });
      toast.success("Témoignage créé");
      setShowCreateTestimonial(false);
      await fetchAll();
    } catch (err) {
      toast.error("Erreur", { description: err?.response?.data?.detail || "Veuillez réessayer." });
    } finally { setSubmitting(false); }
  };

  const onUpdateTestimonial = async (payload) => {
    setSubmitting(true);
    try {
      await axios.put(`${API}/testimonials/${editingTestimonial.id}`, payload, { headers: authHeader() });
      toast.success("Témoignage mis à jour");
      setEditingTestimonial(null);
      await fetchAll();
    } catch (err) {
      toast.error("Erreur", { description: err?.response?.data?.detail || "Veuillez réessayer." });
    } finally { setSubmitting(false); }
  };

  const onDeleteTestimonial = async (t) => {
    if (!window.confirm(`Supprimer le témoignage de "${t.name}" ?`)) return;
    try {
      await axios.delete(`${API}/testimonials/${t.id}`, { headers: authHeader() });
      toast.success("Supprimé");
      await fetchAll();
    } catch (err) {
      toast.error("Erreur", { description: err?.response?.data?.detail || "Erreur" });
    }
  };

  const onTogglePublishTestimonial = async (t) => {
    try {
      await axios.put(`${API}/testimonials/${t.id}`,
        { is_published: !t.is_published },
        { headers: authHeader() });
      toast.success(t.is_published ? "Dépublié" : "Publié");
      await fetchAll();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-[#FAF8F3]">
      <header className="bg-navy-700 text-white">
        <div className="container-edge h-auto py-4 md:h-20 md:py-0 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 md:gap-4 min-w-0">
            <span className="font-display text-lg md:text-xl">ECL Admin</span>
            <span className="overline text-white/60 hidden sm:block truncate">{admin.email}</span>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchAll} className="text-white/80 hover:text-white p-2" aria-label="Rafraîchir" data-testid="admin-refresh">
              <RefreshCw className="h-4 w-4" />
            </button>
            <button onClick={() => { logout(); navigate("/admin/login"); }}
              className="text-[10px] md:text-[11px] uppercase tracking-[0.18em] font-semibold flex items-center gap-2 px-3 py-2 border border-white/30 hover:border-white"
              data-testid="admin-logout">
              <LogOut className="h-4 w-4" />Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div className="container-edge py-6 md:py-10">
        <h1 className="font-display text-2xl md:text-4xl text-navy-700 mb-6 md:mb-8">Tableau de bord</h1>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
          <StatCard icon={Calendar} label="Événements" value={stats?.events} testId="stat-events" />
          <StatCard icon={Users} label="Inscriptions" value={stats?.event_registrations} testId="stat-registrations" />
          <StatCard icon={Handshake} label="Leads partenaires" value={stats?.partner_leads} testId="stat-partners" />
          <StatCard icon={GraduationCap} label="Candidatures" value={stats?.applications} testId="stat-applications" />
          <StatCard icon={MessageSquare} label="Messages" value={stats?.contact_messages} testId="stat-messages" />
          <StatCard icon={BarChart3} label="Quiz" value={stats?.quiz_submissions} testId="stat-quiz" />
        </div>

        <Tabs defaultValue="events" className="w-full">
          <TabsList className="bg-white rounded-none border border-navy-100 h-auto p-1 w-full overflow-x-auto flex no-scrollbar">
            <TabsTrigger value="events" className="rounded-none data-[state=active]:bg-navy-700 data-[state=active]:text-white text-navy-700 px-5 py-2.5 text-xs uppercase tracking-wider" data-testid="tab-events">Événements</TabsTrigger>
            <TabsTrigger value="testimonials" className="rounded-none data-[state=active]:bg-navy-700 data-[state=active]:text-white text-navy-700 px-5 py-2.5 text-xs uppercase tracking-wider" data-testid="tab-testimonials">Témoignages</TabsTrigger>
            <TabsTrigger value="partners" className="rounded-none data-[state=active]:bg-navy-700 data-[state=active]:text-white text-navy-700 px-5 py-2.5 text-xs uppercase tracking-wider" data-testid="tab-partners">Partenaires</TabsTrigger>
            <TabsTrigger value="apps" className="rounded-none data-[state=active]:bg-navy-700 data-[state=active]:text-white text-navy-700 px-5 py-2.5 text-xs uppercase tracking-wider" data-testid="tab-apps">Candidatures</TabsTrigger>
            <TabsTrigger value="contacts" className="rounded-none data-[state=active]:bg-navy-700 data-[state=active]:text-white text-navy-700 px-5 py-2.5 text-xs uppercase tracking-wider" data-testid="tab-contacts">Messages</TabsTrigger>
          </TabsList>

          <TabsContent value="events" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl text-navy-700">Gérer les événements</h2>
              <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogTrigger asChild>
                  <button className="btn-red" data-testid="admin-create-event">
                    <Plus className="h-4 w-4" />Nouveau
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-none bg-white">
                  <DialogHeader>
                    <DialogTitle className="font-display text-2xl text-navy-700">Nouvel événement</DialogTitle>
                    <DialogDescription>Renseignez les informations à publier sur le site.</DialogDescription>
                  </DialogHeader>
                  <EventForm initial={EMPTY_EVENT} onSubmit={onCreate} onClose={() => setShowCreate(false)} submitting={submitting} authHeader={authHeader} />
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid gap-4">
              {events.length === 0 ? (
                <div className="bg-white border border-navy-100 p-10 text-center text-[#888]">
                  Aucun événement pour le moment. Cliquez sur « Nouveau » pour en créer un.
                </div>
              ) : events.map((ev) => (
                <div key={ev.id} className="bg-white border border-navy-100 p-5 flex flex-col gap-4" data-testid={`admin-event-row-${ev.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider ${ev.is_published ? "bg-navy-700 text-white" : "bg-[#eee] text-[#666]"}`}>
                        {ev.is_published ? "Publié" : "Brouillon"}
                      </span>
                      {ev.category && <span className="text-[10px] uppercase tracking-wider text-navy-500">{ev.category}</span>}
                    </div>
                    <div className="font-display text-lg sm:text-xl text-navy-700 break-words">{ev.title}</div>
                    <div className="text-[#666] text-sm flex flex-wrap gap-x-4 gap-y-1 mt-1">
                      <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{ev.event_date}</span>
                      <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{ev.location}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap shrink-0">
                    <Dialog>
                      <DialogTrigger asChild>
                        <button className="btn-outline-navy !py-2 !px-3 text-[10px]" data-testid={`admin-event-regs-${ev.id}`}><Users className="h-3.5 w-3.5" />Inscriptions</button>
                      </DialogTrigger>
                      <DialogContent className="max-w-3xl rounded-none bg-white">
                        <DialogHeader>
                          <DialogTitle className="font-display text-xl text-navy-700">Inscriptions — {ev.title}</DialogTitle>
                          <DialogDescription>Liste des participants enregistrés.</DialogDescription>
                        </DialogHeader>
                        <RegistrationsModal event={ev} authHeader={authHeader} />
                      </DialogContent>
                    </Dialog>
                    <button className="btn-outline-navy !py-2 !px-3 text-[10px]" onClick={() => setEditing(ev)} data-testid={`admin-event-edit-${ev.id}`}>
                      <Pencil className="h-3.5 w-3.5" />Modifier
                    </button>
                    <button onClick={() => onDelete(ev)} className="border border-[#ddd] text-[#a33] hover:bg-[#fdecee] hover:border-[#a33] px-3 py-2 text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1.5"
                      data-testid={`admin-event-delete-${ev.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-none bg-white">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl text-navy-700">Modifier l'événement</DialogTitle>
                  <DialogDescription>Mettez à jour les informations puis enregistrez.</DialogDescription>
                </DialogHeader>
                {editing && <EventForm initial={editing} onSubmit={onUpdate} onClose={() => setEditing(null)} submitting={submitting} authHeader={authHeader} />}
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="testimonials" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl text-navy-700">Gérer les témoignages</h2>
              <Dialog open={showCreateTestimonial} onOpenChange={setShowCreateTestimonial}>
                <DialogTrigger asChild>
                  <button className="btn-red" data-testid="admin-create-testimonial">
                    <Plus className="h-4 w-4" />Nouveau
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-none bg-white">
                  <DialogHeader>
                    <DialogTitle className="font-display text-2xl text-navy-700">Nouveau témoignage</DialogTitle>
                    <DialogDescription>Le témoignage apparaîtra dans la section « Ils en parlent » de la page d'accueil.</DialogDescription>
                  </DialogHeader>
                  <TestimonialForm initial={EMPTY_TESTIMONIAL} onSubmit={onCreateTestimonial} submitting={submitting} />
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid gap-3">
              {testimonials.length === 0 ? (
                <div className="bg-white border border-navy-100 p-10 text-center text-[#888]">
                  Aucun témoignage. Cliquez sur « Nouveau » pour en créer un.
                </div>
              ) : testimonials.map((t) => (
                <div key={t.id} className="bg-white border border-navy-100 p-5 flex flex-col md:flex-row gap-4" data-testid={`admin-testimonial-row-${t.id}`}>
                  <div className="shrink-0">
                    {t.avatar_url ? (
                      <img src={t.avatar_url} alt={t.name} className="w-16 h-16 rounded-full object-cover ring-2 ring-red-500/40" />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-navy-500 to-navy-700 text-white flex items-center justify-center font-display text-xl ring-2 ring-red-500/40">
                        {(t.name || "?").trim().charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider ${t.is_published ? "bg-navy-700 text-white" : "bg-[#eee] text-[#666]"}`}>
                        {t.is_published ? "Publié" : "Brouillon"}
                      </span>
                      <span className="text-[10px] uppercase tracking-wider text-navy-500">Ordre : {t.order}</span>
                    </div>
                    <div className="font-display text-lg text-navy-700 break-words">{t.name}</div>
                    <div className="text-[12px] text-navy-500 uppercase tracking-wider mb-2">
                      {t.role}{t.country ? ` · ${t.country}` : ""}
                    </div>
                    <p className="text-[13.5px] text-[#555] italic leading-relaxed line-clamp-2">« {t.text} »</p>
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0 md:flex-col md:gap-1">
                    <button onClick={() => onTogglePublishTestimonial(t)}
                      className="btn-outline-navy !py-2 !px-3 text-[10px]"
                      data-testid={`admin-testimonial-toggle-${t.id}`}>
                      {t.is_published ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      {t.is_published ? "Dépublier" : "Publier"}
                    </button>
                    <button className="btn-outline-navy !py-2 !px-3 text-[10px]" onClick={() => setEditingTestimonial(t)} data-testid={`admin-testimonial-edit-${t.id}`}>
                      <Pencil className="h-3.5 w-3.5" />Modifier
                    </button>
                    <button onClick={() => onDeleteTestimonial(t)}
                      className="border border-[#ddd] text-[#a33] hover:bg-[#fdecee] hover:border-[#a33] px-3 py-2 text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1.5"
                      data-testid={`admin-testimonial-delete-${t.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <Dialog open={!!editingTestimonial} onOpenChange={(o) => !o && setEditingTestimonial(null)}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-none bg-white">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl text-navy-700">Modifier le témoignage</DialogTitle>
                  <DialogDescription>Mettez à jour les informations puis enregistrez.</DialogDescription>
                </DialogHeader>
                {editingTestimonial && <TestimonialForm initial={editingTestimonial} onSubmit={onUpdateTestimonial} submitting={submitting} authHeader={authHeader} />}
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="partners">
            <Section
              testId="admin-partners-table"
              title="Leads partenaires"
              rows={partners}
              columns={[
                { key: "company", label: "Société" },
                { key: "name", label: "Contact" },
                { key: "email", label: "Email" },
                { key: "phone", label: "Téléphone" },
                { key: "partnership_type", label: "Type" },
                { key: "message", label: "Message", render: (r) => <span className="line-clamp-2 max-w-md inline-block">{r.message}</span> },
                { key: "created_at", label: "Date", render: (r) => new Date(r.created_at).toLocaleDateString("fr-FR") },
              ]}
            />
          </TabsContent>

          <TabsContent value="apps">
            <Section
              testId="admin-apps-table"
              title="Candidatures"
              rows={applications}
              columns={[
                { key: "first_name", label: "Prénom" },
                { key: "last_name", label: "Nom" },
                { key: "email", label: "Email" },
                { key: "program", label: "Programme" },
                { key: "education_level", label: "Niveau" },
                { key: "nationality", label: "Nationalité" },
                { key: "created_at", label: "Date", render: (r) => new Date(r.created_at).toLocaleDateString("fr-FR") },
              ]}
            />
          </TabsContent>

          <TabsContent value="contacts">
            <Section
              testId="admin-contacts-table"
              title="Messages reçus"
              rows={contacts}
              columns={[
                { key: "name", label: "Nom" },
                { key: "email", label: "Email" },
                { key: "type", label: "Type" },
                { key: "subject", label: "Sujet" },
                { key: "message", label: "Message", render: (r) => <span className="line-clamp-2 max-w-md inline-block">{r.message}</span> },
                { key: "created_at", label: "Date", render: (r) => new Date(r.created_at).toLocaleDateString("fr-FR") },
              ]}
            />
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
